import { json2csv } from "json-2-csv"
import toast from "react-hot-toast"
import { AnyAction } from "redux"
import { ThunkAction } from "redux-thunk"
import { requestThunk } from "swiipe.portal.shared"
import {
    applyChangeInTranslationFile,
    calculateTranslationValue,
    getBaseTranslationLanguage,
    getDefaultTranslationContentForFileType,
    mapFilesToTranslationItems,
    mapRowsToTranslationItems,
    orderTranslationFileContentAsBaseFile,
    parseTranslationFileContent,
    stringifyTranslationFileContent,
} from "../../services/translationService"
import { ITranslationContextDto } from "../../type/translations/ITranslationContextDto"
import { ITranslationExportFormat } from "../../type/translations/ITranslationExportFormat"
import { ITranslationFileContent } from "../../type/translations/ITranslationFileContent"
import { ITranslationFileDto } from "../../type/translations/ITranslationFileDto"
import { ITranslationGlossaryEntryDto } from "../../type/translations/ITranslationGlossaryEntryDto"
import { ITranslationItem } from "../../type/translations/ITranslationItem"
import { ITranslationLanguageConfig } from "../../type/translations/ITranslationLanguageConfig"
import { ITranslationRowDto } from "../../type/translations/ITranslationRowDto"
import { ITranslationTagDto } from "../../type/translations/ITranslationTagDto"
import { ITranslationUsage } from "../../type/translations/ITranslationUsage"
import { downloadCsvFile, downloadJsonFile } from "../../util/fileUtil"
import { StoreState } from "../StoreState"
import { ITranslationChange, translationActions, translationSelectors } from "../reducers/translationSlice"
import { endpoints } from "./../../data/endpoints"

export const getTranslationsThunk =
    (languages: string[]): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const result = await dispatch(
            requestThunk<{
                files: ITranslationFileDto[]
                rows: ITranslationRowDto[]
                pendingChanges: ITranslationChange[]
                userChanges: ITranslationChange[]
                tags: ITranslationTagDto[]
                contexts: ITranslationContextDto[]
                glossaryEntries: ITranslationGlossaryEntryDto[]
            }>(endpoints.Content.getTranslations, {
                params: {
                    languages: languages,
                },
            })
        )

        const files = result.files
        const allItems = [...mapFilesToTranslationItems(files), ...mapRowsToTranslationItems(result.rows)]

        dispatch(translationActions.setTranslationFiles(files))
        dispatch(translationActions.setTags(result.tags))
        dispatch(translationActions.setContexts(result.contexts))
        dispatch(translationActions.setGlossaryEntries(result.glossaryEntries))
        dispatch(translationActions.setTranslationItems(allItems))
        dispatch(translationActions.addChanges({ changes: [...result.pendingChanges, ...result.userChanges], reset: true }))
    }

let syncQueueTimeout: NodeJS.Timeout | undefined = undefined

export const syncTranslationChangesThunk =
    (): ThunkAction<Promise<void>, StoreState, null, AnyAction> => async (dispatch, getState) => {
        if (syncQueueTimeout) {
            clearTimeout(syncQueueTimeout)
        }
        syncQueueTimeout = setTimeout(async () => {
            const changes = translationSelectors.changes(getState())
            dispatch(
                requestThunk(endpoints.Content.syncUserChanges, {
                    data: {
                        changes: changes.flatMap((c) =>
                            c.allChanges
                                ? c.allChanges
                                      .filter((a) => a.type !== "PendingUpload")
                                      .map(
                                          (a) =>
                                              ({
                                                  group: c.group,
                                                  itemId: c.itemId,
                                                  key: c.key,
                                                  language: c.language,
                                                  created: a.created,
                                                  type: a.type,
                                                  value: a.value === null || a.value === undefined ? a.value : a.value + "",
                                                  allChanges: undefined,
                                              } satisfies ITranslationChange)
                                      )
                                : c.type === "PendingUpload"
                                ? []
                                : [{ ...c, allChanges: undefined }]
                        ),
                    },
                })
            )
        }, 2000)
    }

export const getTranslationLanguageConfigsThunk =
    (): ThunkAction<Promise<void>, StoreState, null, AnyAction> => async (dispatch, getState) => {
        if (translationSelectors.languageConfigs(getState())) {
            return
        }

        const result = await dispatch(requestThunk<ITranslationLanguageConfig[]>(endpoints.Content.getLanguageConfigs, {}))

        dispatch(translationActions.setLanguageConfigs(result))
    }

export const ensureTranslationUsageThunk =
    (force: boolean): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        if (!force && translationSelectors.usage(getState())) {
            return
        }

        const result = await dispatch(requestThunk<ITranslationUsage>(endpoints.Content.getTranslationUsage, {}))

        dispatch(translationActions.setUsage(result))
    }

export type ETranslationExportType = "json" | "jsonCompressed" | "csv"
export const exportTranslationsThunk =
    (
        type: ETranslationExportType,
        language: string,

        options?: { includeCurrentTranslations: boolean; group?: string }
    ): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        try {
            const selections = translationSelectors.selections(getState()) ?? []

            const fromLanguage = getBaseTranslationLanguage()

            const items = translationSelectors.items(getState())
            const languageConfigs = translationSelectors.languageConfigs(getState())
            if (!items) {
                return
            }
            const changes = translationSelectors.changes(getState())

            const relevantSelections = selections.filter(
                (s) =>
                    (!options?.group || s.group === options.group) &&
                    (!s.languages || s.languages.includes(language)) &&
                    items.some((i) => i.id === s.itemId && i.translations.some((t) => t.language === fromLanguage && !!t.content))
            )
            const fromLanguageCode = languageConfigs?.find((l) => l.language === fromLanguage)?.shortCulture ?? ""
            const toLanguage = language
            const toLanguageCode = languageConfigs?.find((l) => l.language === toLanguage)?.shortCulture ?? ""
            if (relevantSelections.length === 0) {
                toast.error(`${fromLanguageCode}->${toLanguageCode}: Nothing to export`, {
                    position: "bottom-center",
                })
                return
            }

            const exports = relevantSelections.map<ITranslationExportFormat>((s) => {
                const item = items.find((i) => i.id === s.itemId)!
                const value = options?.includeCurrentTranslations
                    ? calculateTranslationValue(
                          item,
                          changes.filter((c) => c.itemId === item.id),
                          toLanguage
                      )
                    : ""
                return {
                    fromLanguageCode,
                    toLanguageCode,
                    fromValue: item.translations.find((t) => t.language === fromLanguage)!.content,
                    toValue: value,
                    key: s.key,
                    group: s.group,
                }
            })

            const fileName = `${fromLanguageCode}-${toLanguageCode}-${new Date().toISOString()}`
            if (type === "csv") {
                const csv = json2csv(exports, { emptyFieldValue: "" })
                downloadCsvFile(csv, fileName)
                toast.success(`Exported ${fileName}.csv`, { position: "bottom-center" })
            } else {
                downloadJsonFile(exports, fileName, type === "jsonCompressed")
                toast.success(`Exported ${fileName}.json`, {
                    position: "bottom-center",
                })
            }
        } catch (err) {
            toast.error(`Failed to export`, {
                position: "bottom-center",
            })
            console.error(err)
        }
    }

export const uploadTranslationChangesToServerThunk =
    (limitToSelections: boolean): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const rows: ITranslationRowDto[] = []
        try {
            const allChanges = translationSelectors.changes(getState())
            const selections = translationSelectors.selections(getState())
            const changes = limitToSelections
                ? allChanges.filter((c) =>
                      selections.some((s) => s.itemId === c.itemId && (!s.languages || s.languages.includes(c.language)))
                  )
                : allChanges

            const items = translationSelectors.items(getState()) ?? []
            const files = translationSelectors.files(getState()) ?? []

            const baseLanguage = getBaseTranslationLanguage()

            const changedFiles: (ITranslationFileDto & { parsed: ITranslationFileContent })[] = []

            for (let i = 0; i < changes.length; i++) {
                const change = changes[i]
                const item = items.find((i) => i.id === change.itemId)
                if (item?.sourceType === "row") {
                    rows.push({
                        group: item.group,
                        isFlat: false,
                        key: item.key,
                        label: item.label,
                        language: change.language,
                        translation: change.value,
                    } satisfies ITranslationRowDto)
                    continue
                }

                let file = changedFiles.find((f) => f.group === change.group && f.language === change.language)
                if (!file) {
                    let originalFile: ITranslationFileDto | undefined = files.find(
                        (f) => f.group === change.group && f.language === change.language
                    )
                    if (!originalFile) {
                        const otherFileInGroup = files.find((f) => f.group === change.group)
                        if (!otherFileInGroup) {
                            continue
                        }
                        originalFile = {
                            ...otherFileInGroup,
                            language: change.language,
                            content: getDefaultTranslationContentForFileType(otherFileInGroup.fileType),
                        }
                    }
                    changedFiles.push({ ...originalFile, parsed: parseTranslationFileContent(originalFile) })
                    file = changedFiles.find((f) => f.group === change.group && f.language === change.language)
                }
                if (!file) {
                    continue
                }
                applyChangeInTranslationFile(file.parsed, change)
            }
            const filesToUpload = changedFiles.map((cf) => {
                const baseFile =
                    files.find((f) => f.group === cf.group && f.language === baseLanguage) ||
                    files.find((f) => f.group === cf.group && f.language !== cf.language)
                if (baseFile) {
                    cf.parsed = orderTranslationFileContentAsBaseFile(
                        parseTranslationFileContent(baseFile),
                        cf.parsed,
                        cf.fileType
                    )
                }
                const newFile: ITranslationFileDto = { ...cf }
                newFile.content = stringifyTranslationFileContent(cf, cf.parsed)
                return newFile
            })
            await dispatch(
                requestThunk<ITranslationLanguageConfig[]>(endpoints.Content.updateTranslations, {
                    data: {
                        pullRequestTitle: "Update Translations",
                        changes: changes,
                        files: filesToUpload,
                        rows: rows,
                    },
                })
            )
        } catch (err) {
            toast.error(`Failed to upload`, {
                position: "bottom-center",
            })
            console.error(err)
            return
        }

        toast.success("The update was successful", { position: "bottom-center" })
        if (rows.length > 0) {
            toast.success("Fetching new state", { position: "bottom-center" })
            const languageConfigs = translationSelectors.languageConfigs(getState()) ?? []
            try {
                await dispatch(getTranslationsThunk(languageConfigs.map((c) => c.language)))
                await dispatch(syncTranslationChangesThunk())
                toast.success("New state was fetched", { position: "bottom-center" })
            } catch (err) {
                toast.error("Failed to fetch new state", { position: "bottom-center" })
            }
        }
    }

export const addTranslationTagThunk =
    (tag: ITranslationTagDto): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const result = await dispatch(
            requestThunk<{ tag: ITranslationTagDto }>(endpoints.Content.addTag, {
                data: {
                    tag,
                },
            })
        )
        if (result.tag) {
            dispatch(translationActions.addTags([result.tag]))
        }
    }

export const deleteTranslationTagThunk =
    (tag: ITranslationTagDto): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        await dispatch(
            requestThunk(endpoints.Content.deleteTag, {
                data: {
                    tagId: tag.tagId,
                },
            })
        )

        dispatch(translationActions.removeTag(tag.tagId))
    }

export const changeTranslationContextThunk =
    (context: ITranslationContextDto): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const newDto = await dispatch(
            requestThunk<ITranslationContextDto>(endpoints.Content.changeContext, {
                data: {
                    ...context,
                },
            })
        )

        const contexts = translationSelectors.contexts(getState()) ?? []
        const index = contexts.findIndex((c) => c.contextId === context.contextId)
        if (index >= 0) {
            dispatch(translationActions.setContexts([...contexts.slice(0, index), newDto, ...contexts.slice(index + 1)]))
        } else {
            dispatch(translationActions.setContexts([newDto, ...(contexts ?? [])]))
        }
    }

export const setTranslationGlossaryEntriesForLanguageThunk =
    (entries: ITranslationGlossaryEntryDto[]): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        if (entries.length === 0) {
            return
        }
        const tasks = entries.map((e) =>
            dispatch(
                requestThunk<ITranslationContextDto>(endpoints.Content.setGlossaryEntry, {
                    data: {
                        ...e,
                    },
                })
            )
        )
        await Promise.all(tasks)

        const glossaryEntries = translationSelectors.glossaryEntries(getState()) ?? {}
        const entriesForLang = glossaryEntries?.[entries[0].toLanguage] ?? []
        const keepEntriesForLang = entriesForLang.filter((oldE) => !entries.find((e) => e.fromText === oldE.fromText))
        dispatch(
            translationActions.setGlossaryEntriesForLangauge({
                language: entries[0].toLanguage,
                entries: [...keepEntriesForLang, ...entries],
            })
        )
    }

export const removeTranslationGlossaryEntryThunk =
    (entry: ITranslationGlossaryEntryDto): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        await dispatch(
            requestThunk<ITranslationContextDto>(endpoints.Content.removeGlossaryEntry, {
                data: {
                    ...entry,
                },
            })
        )

        const glossaryEntries = translationSelectors.glossaryEntries(getState()) ?? {}
        const entriesForLang = glossaryEntries?.[entry.toLanguage] ?? []
        const keepEntriesForLang = entriesForLang.filter((oldE) => oldE.fromText !== entry.fromText)
        dispatch(
            translationActions.setGlossaryEntriesForLangauge({
                language: entry.toLanguage,
                entries: [...keepEntriesForLang],
            })
        )
    }

export const translateTextsThunk =
    (
        items: ITranslationItem[],
        translateLanguage: string,
        contextId: string
    ): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        const contexts = translationSelectors.contexts(getState())
        const context = contexts?.find((c) => c.contextId === contextId)

        const texts = items.map((i) => i.translations.find((t) => t.language === getBaseTranslationLanguage())!.content)

        const languageConfigs = translationSelectors.languageConfigs(getState()) ?? []
        const toLang = languageConfigs.find((lc) => lc.language === translateLanguage)!.shortCulture
        const response = await dispatch(
            requestThunk<{
                resp: {
                    translations: {
                        billed_characters: number
                        detected_source_language: string
                        model_type_used: string
                        text: string
                    }[]
                }
            }>(endpoints.Content.translate, {
                data: {
                    texts: texts,
                    toLanguage: toLang,
                    context: context?.context,
                },
            })
        )

        const changes: ITranslationChange[] = response.resp.translations
            .map((t, index) => {
                const item = items[index]
                return {
                    group: item.group,
                    itemId: item.id,
                    key: item.key,
                    language: translateLanguage,
                    value: t.text,
                    type: "TranslationService",
                    created: new Date().toISOString(),
                } satisfies ITranslationChange
            })
            .filter((c) => !!c.value)

        dispatch(translationActions.addChanges({ changes }))
        dispatch(syncTranslationChangesThunk())
        dispatch(ensureTranslationUsageThunk(true))
    }
